import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import SetupErrorMessage from './SetupErrorMessage';
import { researchAPI } from '../services/api';

const ResearchRunDialog = ({ isOpen, onClose, buyerIds, onQueued, initialProfileId }) => {
  const [profiles, setProfiles] = useState(null);
  const [profileId, setProfileId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setError('');
    setLoadFailed(false);
    setProfiles(null);
    researchAPI.listProfiles()
      .then((res) => {
        if (cancelled) return;
        const list = (res && res.data && res.data.profiles) || [];
        setProfiles(list);
        const wanted = initialProfileId != null
          ? list.find((p) => String(p.id) === String(initialProfileId)) : null;
        const def = wanted || list.find((p) => p.is_default) || list[0];
        setProfileId(def ? String(def.id) : '');
      })
      .catch(() => {
        if (!cancelled) { setProfiles([]); setLoadFailed(true); }
      });
    return () => { cancelled = true; };
  }, [isOpen, initialProfileId, reloadKey]);

  // Esc closes only this dialog (the slide-over ignores Esc while it reports a dialog open).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (!submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  const ids = buyerIds || [];
  const count = ids.length;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await researchAPI.run({
        buyer_ids: ids,
        profile_id: Number(profileId),
        confirm: count > 1,
      });
      if (!mounted.current) return;
      if (onQueued) onQueued((res && res.data && res.data.queued) || 0);
      onClose();
    } catch (err) {
      if (mounted.current) {
        setError((err && err.response && err.response.data && err.response.data.error)
          || (err && err.message) || 'Could not start research');
      }
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  const noProfiles = profiles !== null && profiles.length === 0 && !loadFailed;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI Research" showFooter={false} size="small">
      {profiles === null && <p className="research-note">Loading…</p>}
      {noProfiles && (
        <p className="research-note">
          You need a research profile first.{' '}
          <Link to="/settings#ai" onClick={onClose}>Create one in Settings</Link>.
        </p>
      )}
      {loadFailed && (
        <p className="research-error" role="alert">
          Could not load your profiles.{' '}
          <button type="button" className="research-link" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
        </p>
      )}
      {profiles !== null && !noProfiles && !loadFailed && (
        <>
          <label htmlFor="research-profile" className="research-label">Score against</label>
          <select
            id="research-profile"
            className="research-select"
            value={profileId}
            onChange={(e) => setProfileId(e.target.value)}
          >
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (default)' : ''}</option>
            ))}
          </select>
          <p className="research-note">
            {count === 1
              ? 'This reads the prospect\'s website and sends it to your AI provider using your own key.'
              : `This will use your own AI key for ${count} prospects and may cost money. Continue?`}
          </p>
        </>
      )}
      {error && (
        <p className="research-error" role="alert">
          <SetupErrorMessage message={error} />
        </p>
      )}
      <div className="research-dialog-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={submitting || noProfiles || loadFailed || profiles === null || !profileId || count === 0}
        >
          {submitting ? 'Starting…' : count > 1 ? `Research ${count} prospects` : 'Research'}
        </button>
      </div>
    </Modal>
  );
};

export default ResearchRunDialog;
